import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building,
  CheckCircle,
  XCircle,
  Eye,
  Clock,
  Search,
  Filter,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/components/auth-provider";
import { Property } from "@/types/types";
import { searchProperties, updateProperty } from "@/lib/property-service";
import { notifyPropertyApproval } from "@/lib/notification-service";
import { formatNaira } from "@/lib/utils";

export default function AdminApprovalsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "pending" | "rejected"
  >("pending");
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null
  );
  const [actionDialog, setActionDialog] = useState<"approve" | "reject" | null>(
    null
  );
  const [rejectionReason, setRejectionReason] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    loadProperties();
  }, [statusFilter]);

  async function loadProperties() {
    try {
      setLoading(true);
      const allPropertiesResult = await searchProperties({
        includeUnapproved: true, // Admin can see all
      });

      let filtered = allPropertiesResult.data;

      // Filter by approval status
      if (statusFilter === "pending") {
        filtered = filtered.filter((p: any) => p.approvalStatus === "pending");
      } else if (statusFilter === "rejected") {
        filtered = filtered.filter((p: any) => p.approvalStatus === "rejected");
      }

      setProperties(filtered);
    } catch (error) {
      console.error("Error loading properties:", error);
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  }

  const filteredProperties = properties.filter(
    (property) =>
      property.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      property.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  async function handleApprove(property: Property) {
    if (!user) return;

    setIsProcessing(true);
    try {
      await updateProperty(property.id, {
        approvalStatus: "approved",
      });

      // Send notification to landlord
      await notifyPropertyApproval(
        property.listerId,
        property.id,
        property.title,
        true
      );

      toast.success("Property approved successfully!", {
        description: `${property.title} is now visible in the marketplace.`,
      });

      setActionDialog(null);
      setSelectedProperty(null);
      loadProperties();
    } catch (error) {
      console.error("Error approving property:", error);
      toast.error("Failed to approve property");
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleReject(property: Property) {
    if (!user || !rejectionReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    setIsProcessing(true);
    try {
      await updateProperty(property.id, {
        approvalStatus: "rejected",
      });

      // Send notification to landlord with reason
      await notifyPropertyApproval(
        property.listerId,
        property.id,
        property.title,
        false,
        rejectionReason
      );

      toast.success("Property rejected", {
        description: "Landlord has been notified.",
      });

      setActionDialog(null);
      setSelectedProperty(null);
      setRejectionReason("");
      loadProperties();
    } catch (error) {
      console.error("Error rejecting property:", error);
      toast.error("Failed to reject property");
    } finally {
      setIsProcessing(false);
    }
  }

  const pendingCount = properties.filter(
    (p) => p.approvalStatus === "pending"
  ).length;
  const approvedCount = properties.filter(
    (p) => p.approvalStatus === "approved"
  ).length;
  const rejectedCount = properties.filter(
    (p) => p.approvalStatus === "rejected"
  ).length;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Property Approvals</h1>
        <p className="text-muted-foreground">
          Review and approve property listings for the marketplace
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Pending Review
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingCount}</div>
            <p className="text-xs text-muted-foreground">Awaiting approval</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{approvedCount}</div>
            <p className="text-xs text-muted-foreground">Live in marketplace</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rejected</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{rejectedCount}</div>
            <p className="text-xs text-muted-foreground">Needs revision</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search properties..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <Select
          value={statusFilter}
          onValueChange={(value: any) => setStatusFilter(value)}
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Properties</SelectItem>
            <SelectItem value="pending">Pending Only</SelectItem>
            <SelectItem value="rejected">Rejected Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Properties Table */}
      <Card>
        <CardHeader>
          <CardTitle>Property Listings</CardTitle>
          <CardDescription>
            {filteredProperties.length} properties found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading properties...</div>
          ) : filteredProperties.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building className="mx-auto h-12 w-12 mb-4 opacity-50" />
              <p>No properties found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Rent</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProperties.map((property) => (
                    <TableRow key={property.id}>
                      <TableCell className="font-medium">
                        {property.title}
                      </TableCell>
                      <TableCell className="capitalize">
                        {property.type.replace("-", " ")}
                      </TableCell>
                      <TableCell>{property.address}</TableCell>
                      <TableCell>{formatNaira(property.rent)}/mo</TableCell>
                      <TableCell>
                        {property.approvalStatus === "pending" && (
                          <Badge variant="secondary">
                            <Clock className="h-3 w-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                        {property.approvalStatus === "approved" && (
                          <Badge variant="default" className="bg-green-600">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Approved
                          </Badge>
                        )}
                        {property.approvalStatus === "rejected" && (
                          <Badge variant="destructive">
                            <XCircle className="h-3 w-3 mr-1" />
                            Rejected
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(property.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              navigate(`/properties/${property.id}`)
                            }
                          >
                            <Eye className="h-4 w-4" />
                          </Button>

                          {property.approvalStatus === "pending" && (
                            <>
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => {
                                  setSelectedProperty(property);
                                  setActionDialog("approve");
                                }}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                  setSelectedProperty(property);
                                  setActionDialog("reject");
                                }}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}

                          {property.approvalStatus === "rejected" && (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => {
                                setSelectedProperty(property);
                                setActionDialog("approve");
                              }}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog
        open={actionDialog === "approve" && selectedProperty !== null}
        onOpenChange={(open) => {
          if (!open) {
            setActionDialog(null);
            setSelectedProperty(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Property</DialogTitle>
            <DialogDescription>
              Are you sure you want to approve this property? It will be visible
              in the public marketplace.
            </DialogDescription>
          </DialogHeader>

          {selectedProperty && (
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-2">{selectedProperty.title}</h3>
              <p className="text-sm text-muted-foreground mb-1">
                {selectedProperty.address}
              </p>
              <p className="text-sm">
                <span className="font-medium">Rent:</span>{" "}
                {formatNaira(selectedProperty.rent)}/month
              </p>
              <p className="text-sm">
                <span className="font-medium">Type:</span>{" "}
                <span className="capitalize">
                  {selectedProperty.type.replace("-", " ")}
                </span>
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActionDialog(null);
                setSelectedProperty(null);
              }}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                selectedProperty && handleApprove(selectedProperty)
              }
              disabled={isProcessing}
            >
              {isProcessing ? "Approving..." : "Approve Property"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={actionDialog === "reject" && selectedProperty !== null}
        onOpenChange={(open) => {
          if (!open) {
            setActionDialog(null);
            setSelectedProperty(null);
            setRejectionReason("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Property</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this property. The landlord
              will be notified.
            </DialogDescription>
          </DialogHeader>

          {selectedProperty && (
            <div className="space-y-4">
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">{selectedProperty.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {selectedProperty.address}
                </p>
              </div>

              <div>
                <Label htmlFor="reason">Rejection Reason *</Label>
                <Textarea
                  id="reason"
                  placeholder="e.g., Images are unclear, missing property details, incorrect pricing..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  className="mt-1.5"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActionDialog(null);
                setSelectedProperty(null);
                setRejectionReason("");
              }}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedProperty && handleReject(selectedProperty)}
              disabled={isProcessing || !rejectionReason.trim()}
            >
              {isProcessing ? "Rejecting..." : "Reject Property"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
